import React, { useEffect, useMemo, useState } from "react";
import {
    ArrowRightOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    CloseCircleOutlined,
    FileTextOutlined,
    PlusOutlined,
} from "@ant-design/icons";
import {
    Button,
    Card,
    Col,
    Empty,
    Row,
    Space,
    Statistic,
    Typography,
    message,
} from "antd";
import { Link } from "react-router-dom";
import AppLayout from "../components/layout/AppLayout";
import CustomerRequestList from "../components/transactions/CustomerRequestList";
import { getMyCustomerTransactionSchedules } from "../api/customerTransactionApi";
import { getStoredUser } from "../utils/authStorage";

const { Title, Paragraph, Text } = Typography;

function buildStats(rows) {
    return {
        total: rows.length,
        submitted: rows.filter((x) => x.customerSubmissionStatus === "SUBMITTED").length,
        reviewed: rows.filter((x) => x.customerSubmissionStatus === "REVIEWED").length,
        approved: rows.filter((x) => x.customerSubmissionStatus === "APPROVED").length,
        rejected: rows.filter((x) => x.customerSubmissionStatus === "REJECTED").length,
    };
}

export default function DashboardPage() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const user = getStoredUser();

    useEffect(() => {
        const load = async () => {
            try {
                setLoading(true);
                const data = await getMyCustomerTransactionSchedules();
                setRows(Array.isArray(data) ? data : []);
            } catch (error) {
                message.error(error.message || "Failed to load dashboard data");
            } finally {
                setLoading(false);
            }
        };

        load();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            const data = await getMyCustomerTransactionSchedules();
            setRows(Array.isArray(data) ? data : []);
        } catch (error) {
            message.error(error.message || "Failed to load dashboard data");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    const stats = useMemo(() => buildStats(rows), [rows]);
    const recentRows = useMemo(() => rows.slice(0, 5), [rows]);

    return (
        <AppLayout>
            <Space direction="vertical" size={20} style={{ width: "100%" }}>
                <Card>
                    <Row gutter={[16, 16]} align="middle" justify="space-between">
                        <Col xs={24} md={16}>
                            <Space direction="vertical" size={4}>
                                <Title level={2} style={{ margin: 0 }}>
                                    Dashboard
                                </Title>
                                <Paragraph style={{ margin: 0 }} type="secondary">
                                    Welcome{user?.fullName ? `, ${user.fullName}` : ""}. Review recent requests,
                                    track statuses, and create a new submission quickly.
                                </Paragraph>
                            </Space>
                        </Col>

                        <Col xs={24} md="auto">
                            <Space wrap>
                                <Button type="primary" icon={<PlusOutlined />}>
                                    <Link to="/requests/new">New Request</Link>
                                </Button>
                                <Button icon={<FileTextOutlined />}>
                                    <Link to="/requests">All Requests</Link>
                                </Button>
                            </Space>
                        </Col>
                    </Row>
                </Card>

                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} lg={6}>
                        <Card>
                            <Statistic title="Total Requests" value={stats.total} prefix={<FileTextOutlined />} />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                        <Card>
                            <Statistic title="Submitted" value={stats.submitted} prefix={<ClockCircleOutlined />} />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                        <Card>
                            <Statistic title="Approved" value={stats.approved} prefix={<CheckCircleOutlined />} />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                        <Card>
                            <Statistic title="Rejected" value={stats.rejected} prefix={<CloseCircleOutlined />} />
                        </Card>
                    </Col>
                </Row>

                <Row gutter={[16, 16]}>
                    <Col xs={24} xl={17}>
                        <Card
                            title="Recent Requests"
                            extra={
                                <Link to="/requests">
                                    View all <ArrowRightOutlined />
                                </Link>
                            }
                        >
                            {rows.length === 0 && !loading ? (
                                <Empty
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    description="No requests yet"
                                >
                                    <Button type="primary">
                                        <Link to="/requests/new">Create first request</Link>
                                    </Button>
                                </Empty>
                            ) : (
                                <CustomerRequestList
                                    data={recentRows}
                                    loading={loading}
                                    onDeleted={loadData}
                                    compact
                                    pageSize={5}
                                />
                            )}
                        </Card>
                    </Col>

                    <Col xs={24} xl={7}>
                        <Space direction="vertical" size={16} style={{ width: "100%" }}>
                            <Card title="Quick Guide">
                                <Space direction="vertical" size={8}>
                                    <Text>1. Create a request and fill all payment fields.</Text>
                                    <Text>2. Upload invoice and contract PDF files.</Text>
                                    <Text>3. Track status changes from My Requests.</Text>
                                </Space>
                            </Card>

                            <Card title="Review Queue">
                                <Space direction="vertical" size={6}>
                                    <Text>Total in review flow: {stats.submitted + stats.reviewed}</Text>
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        Submitted requests are waiting for review. Reviewed requests are still in progress.
                                    </Text>
                                </Space>
                            </Card>
                        </Space>
                    </Col>
                </Row>
            </Space>
        </AppLayout>
    );
}
